import React, { useState, useRef } from 'react';
import { 
  FileCheck, Upload, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Edit3, Trash2, CheckSquare, AlignLeft, Sparkles
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';
import { LocalModelToggle } from './LocalModelToggle';

interface DocumentCorrectorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

export default function DocumentCorrector({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: DocumentCorrectorProps) {
  const isUk = language === 'uk';
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });

  const [isExtracting, setIsExtracting] = useState(false);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [correctedText, setCorrectedText] = useState<string | null>(null);
  const [errorAnalysis, setErrorAnalysis] = useState<string | null>(null);
  
  const [activeResultTab, setActiveResultTab] = useState<'analysis' | 'corrected'>('analysis');
  const [copiedText, setCopiedText] = useState(false);
  const [copiedAnalysis, setCopiedAnalysis] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [textStyle, setTextStyle] = useState<'dstu' | 'sans' | 'mono'>('dstu');

  // Helper: Strip wrapping code block fences (```markdown ... ```)
  const cleanCodeFences = (str: string): string => {
    if (!str) return '';
    let cleaned = str.trim();
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```[a-zA-Z]*\n?/, '').replace(/\n?```$/, '');
    }
    return cleaned.trim();
  };

  // Helper: Render inline markdown formatting (**bold**, *italic*, `code`, ~~strike~~)
  const renderInlineMarkdown = (content: string): React.ReactNode => {
    if (!content) return null;
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.*?)\*\*|\*(.*?)\*|`(.*?)`|~~(.*?)~~)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      if (match[2] !== undefined) {
        parts.push(<strong key={match.index} className="font-bold text-slate-900">{match[2]}</strong>);
      } else if (match[3] !== undefined) {
        parts.push(<em key={match.index} className="italic text-slate-800">{match[3]}</em>);
      } else if (match[4] !== undefined) {
        parts.push(<code key={match.index} className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-teal-700 font-mono text-[11px]">{match[4]}</code>);
      } else if (match[5] !== undefined) {
        parts.push(<del key={match.index} className="line-through text-red-500">{match[5]}</del>);
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }
    return parts.length > 0 ? parts : content;
  };

  // Helper: Full Markdown Report Renderer for Error Analysis (Tables, Headings, Lists, Cards)
  const renderMarkdownReport = (rawMarkdown: string) => {
    const cleaned = cleanCodeFences(rawMarkdown);
    const lines = cleaned.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // 1. Table Detection
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }

        if (tableLines.length >= 2) {
          const parseRow = (row: string) => {
            const trimmedRow = row.replace(/^\||\|$/g, '');
            return trimmedRow.split(/(?<!\\)\|/).map(c => c.replace(/\\\|/g, '|').trim());
          };

          const headers = parseRow(tableLines[0]);
          const dataRows = tableLines.slice(1).filter(r => !r.includes('---'));

          elements.push(
            <div key={`table-${i}`} className="my-4 overflow-x-auto rounded-lg border border-slate-200 shadow-2xs bg-white">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 border-b border-slate-200 text-slate-800 font-bold font-mono text-[11px]">
                    {headers.map((h, idx) => (
                      <th key={idx} className="p-3 uppercase tracking-wider">{renderInlineMarkdown(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans">
                  {dataRows.map((rowStr, rIdx) => {
                    const cells = parseRow(rowStr);
                    return (
                      <tr key={rIdx} className="hover:bg-slate-50/80 transition-colors">
                        {cells.map((cell, cIdx) => (
                          <td key={cIdx} className="p-3 text-slate-700 leading-relaxed align-top">
                            {renderInlineMarkdown(cell)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }

      // 2. Headings
      if (trimmed.startsWith('### ')) {
        elements.push(
          <h4 key={`h3-${i}`} className="text-xs font-bold font-mono text-teal-600 tracking-wider uppercase border-b border-slate-100 pb-1 mt-5 mb-2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
            {renderInlineMarkdown(trimmed.replace(/^###\s+/, ''))}
          </h4>
        );
        i++;
        continue;
      }
      if (trimmed.startsWith('## ')) {
        elements.push(
          <h3 key={`h2-${i}`} className="text-sm font-bold font-mono text-slate-800 tracking-wide uppercase border-b border-slate-200 pb-1.5 mt-6 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-xs bg-teal-500" />
            {renderInlineMarkdown(trimmed.replace(/^##\s+/, ''))}
          </h3>
        );
        i++;
        continue;
      }
      if (trimmed.startsWith('# ')) {
        elements.push(
          <h2 key={`h1-${i}`} className="text-base font-black font-mono text-slate-900 tracking-wide uppercase border-b-2 border-teal-500 pb-2 mt-6 mb-4">
            {renderInlineMarkdown(trimmed.replace(/^#\s+/, ''))}
          </h2>
        );
        i++;
        continue;
      }

      // 3. Blockquotes / Callouts
      if (trimmed.startsWith('> ')) {
        elements.push(
          <blockquote key={`bq-${i}`} className="border-l-4 border-amber-400 bg-amber-50/60 p-3 my-3 rounded-r text-xs text-amber-950 leading-relaxed shadow-2xs">
            {renderInlineMarkdown(trimmed.replace(/^>\s+/, ''))}
          </blockquote>
        );
        i++;
        continue;
      }

      // 4. Bullet list items (* or -)
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        const listContent = trimmed.replace(/^[\*\-]\s+/, '');
        elements.push(
          <div key={`li-${i}`} className="flex gap-2.5 items-start my-1.5 pl-2 text-xs text-slate-700 leading-relaxed">
            <span className="text-teal-500 shrink-0 font-bold mt-0.5">•</span>
            <div className="flex-1">{renderInlineMarkdown(listContent)}</div>
          </div>
        );
        i++;
        continue;
      }

      // 5. Numbered list items (1. 2. etc)
      const numMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
      if (numMatch) {
        elements.push(
          <div key={`num-${i}`} className="flex gap-2.5 items-start my-1.5 pl-2 text-xs text-slate-700 leading-relaxed">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-teal-100 text-teal-700 text-[10px] font-bold shrink-0 mt-0.5">
              {numMatch[1]}
            </span>
            <div className="flex-1">{renderInlineMarkdown(numMatch[2])}</div>
          </div>
        );
        i++;
        continue;
      }

      // 6. Empty lines
      if (!trimmed) {
        elements.push(<div key={`space-${i}`} className="h-2" />);
        i++;
        continue;
      }

      // 7. Standard Paragraph
      elements.push(
        <p key={`p-${i}`} className="text-xs text-slate-700 leading-relaxed my-1 font-sans">
          {renderInlineMarkdown(line)}
        </p>
      );
      i++;
    }

    return <div className="space-y-1">{elements}</div>;
  };

  // Helper: Render Corrected Text with selectable styles and clean document layout
  const renderCorrectedTextPreview = (rawText: string) => {
    const cleaned = cleanCodeFences(rawText);

    if (textStyle === 'mono') {
      return (
        <div className="p-4 bg-slate-900 border border-slate-800 rounded-lg font-mono text-xs text-teal-300 leading-relaxed whitespace-pre-wrap select-text overflow-x-auto shadow-inner">
          {cleaned}
        </div>
      );
    }

    if (textStyle === 'sans') {
      return (
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg font-sans text-sm text-slate-800 leading-relaxed space-y-3 select-text shadow-2xs">
          {renderMarkdownReport(cleaned)}
        </div>
      );
    }

    // DSTU / Document style (Times New Roman 14pt, 1.5 line height, document paper look)
    return (
      <div className="p-8 bg-white border border-slate-200 rounded-lg shadow-sm font-serif text-[15px] leading-relaxed text-slate-900 select-text space-y-4 max-w-none">
        {renderMarkdownReport(cleaned)}
      </div>
    );
  };

  // File Upload and Text Extraction
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setFileSize(file.size);
    setError(null);
    setIsExtracting(true);
    setCorrectedText(null);
    setErrorAnalysis(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка вилучення тексту з файлу.' : 'Failed to extract text from the file.'));
      }

      const data = await response.json();
      setText(data.text);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося прочитати файл.' : 'Failed to read file.'));
      setFileName(null);
      setFileSize(null);
    } finally {
      setIsExtracting(false);
    }
  };

  // Run Document Correction through Gemini
  const handleCorrectDocument = async () => {
    if (!text.trim()) return;

    setIsCorrecting(true);
    setError(null);
    setCorrectedText(null);
    setErrorAnalysis(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/correct-document', {
        method: 'POST',
        headers,
        body: JSON.stringify({ text, useEnterpriseContext, useLocalModel })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка виправлення тексту моделі.' : 'Failed to correct text.'));
      }

      const data = await response.json();
      setCorrectedText(data.correctedText);
      setErrorAnalysis(data.errorAnalysis);
      setActiveResultTab('analysis'); // Default to analysis tab to show errors
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка при аналізі правопису.' : 'An error occurred during spellcheck analysis.'));
    } finally {
      setIsCorrecting(false);
    }
  };

  // Download Corrected Document as Word compatible .docx file
  const handleDownloadWord = async () => {
    if (!correctedText) return;

    try {
      const name = fileName ? 'corrected-' + fileName.split('.')[0] : 'corrected-document';
      
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: correctedText,
          fileName: name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate DOCX file.');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}.docx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      alert(isUk ? 'Помилка при завантаженні файлу .docx' : 'Failed to download DOCX file');
    }
  };

  // Download Error Analysis Report as Markdown
  const handleDownloadAnalysis = () => {
    if (!errorAnalysis) return;

    const blob = new Blob([errorAnalysis], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `error-analysis-${Date.now()}.md`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyText = (content: string, type: 'text' | 'analysis') => {
    navigator.clipboard.writeText(content);
    if (type === 'text') {
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);
    } else {
      setCopiedAnalysis(true);
      setTimeout(() => setCopiedAnalysis(false), 2000);
    }
  };

  const handleClear = () => {
    setText('');
    setFileName(null);
    setFileSize(null);
    setCorrectedText(null);
    setErrorAnalysis(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in font-sans">
      
      {/* Go Back & Title Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-emerald-400 font-mono text-xs font-bold rounded transition-all cursor-pointer flex items-center gap-1.5"
          >
            🏠 {isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-mono text-xs font-bold rounded border border-slate-300 transition-all cursor-pointer flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
            <span>{isUk ? '← До Офісного Центру' : '← Back to Office Hub'}</span>
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
          <div className="text-right hidden sm:block">
            <span className="text-[9px] font-bold font-mono uppercase bg-teal-50 border border-teal-200 px-2 py-0.5 rounded text-teal-600">
              {isUk ? 'РЕДАКТУВАННЯ & ПРАВОПИС' : 'PROOFREADING & GRAMMAR'}
            </span>
            <h2 className="text-sm font-bold font-mono text-slate-800 uppercase tracking-wide mt-1">
              {isUk ? 'Агент «Коректор»' : 'AI Proofreader'}
            </h2>
          </div>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 mb-2 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">Активовано контекст підприємства: </span>
              <span className="font-mono">{enterpriseName || "База знань компанії"}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            База знань
          </span>
        </div>
      )}

      {/* Main Core Layout: Input on left/top, Output on right/bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left Column: Form & File Input (span 5) */}
        <div className="lg:col-span-5 space-y-6">
          
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-xs space-y-4">
            <h3 className="text-xs font-bold font-mono uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-teal-500" />
              <span>{isUk ? '1. Завантаження або Введення' : '1. Upload or Input Text'}</span>
            </h3>

            {/* Document Uploader Area */}
            <div className="border border-dashed border-slate-300 hover:border-teal-400 rounded-lg p-4 text-center cursor-pointer transition-colors relative bg-slate-50">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileUpload} 
                accept=".doc,.docx,.pdf,.txt" 
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="space-y-1.5 flex flex-col items-center">
                <Upload className="w-6 h-6 text-slate-400" />
                <p className="text-[11px] font-bold text-slate-700">
                  {isUk ? 'Завантажити документ українською' : 'Upload document in Ukrainian'}
                </p>
                <p className="text-[9px] text-slate-400">
                  Word (.doc, .docx), PDF (.pdf) або TXT (.txt) до 15 MB
                </p>
              </div>
            </div>

            {/* Quick Demo Sample Document Button */}
            <button
              type="button"
              onClick={() => {
                setFileName("sample_document_for_correction.docx");
                setFileSize(12400);
                setText(`ЗАТВЕРДЖУЮ
Директор ТОВ "Агротехпостач"
Петренко Іван Іванович
"15" січень 2026 року

АКТ ПРИЙОМУ-ПЕРЕДАЧІ РОБІТ
з згідно договору № 45/2026 від 10 січня 2026р.

Ми нижчепідписавшіся представник Заказчика і Ісполнитель склали цей акт про те що Ісполнитель виповнив слідуючі роботи по ремонту автотранспорту.
Всі роботи виконано в повному обємі і в установлені строки. Сторони не мають взаємних претензій один до одного.

Підписи сторін:
Заказчик: _____________ І.І. Петренко
Виконавець: _____________ О.В. Сидоренко`);
                setError(null);
              }}
              className="w-full py-2 px-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-2xs"
            >
              <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
              <span>{isUk ? "⚡ Завантажити демо-документ з помилками для тесту" : "⚡ Load demo text with errors for 1-click test"}</span>
            </button>

            {/* Uploaded File Info */}
            {fileName && (
              <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded text-[10px] font-mono">
                <div className="flex items-center gap-2 truncate">
                  <FileText className="w-4 h-4 text-teal-500 shrink-0" />
                  <span className="truncate text-slate-700 font-bold">{fileName}</span>
                  <span className="text-slate-400 shrink-0">({(fileSize! / 1024).toFixed(1)} KB)</span>
                </div>
                <button 
                  onClick={handleClear} 
                  className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-red-500 transition-colors"
                  title={isUk ? 'Очистити все' : 'Clear all'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Textarea for Text input */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                  {isUk ? 'Текст документа:' : 'Document text:'}
                </label>
                {text && (
                  <span className="text-[9px] font-mono text-slate-400">
                    {text.length} {isUk ? 'симв.' : 'chars'}
                  </span>
                )}
              </div>
              
              <div className="relative">
                {isExtracting ? (
                  <div className="absolute inset-0 bg-slate-50/80 flex flex-col items-center justify-center rounded border border-slate-200">
                    <RefreshCw className="w-5 h-5 text-teal-500 animate-spin" />
                    <span className="text-[9px] font-mono font-bold text-slate-500 uppercase mt-2">Вилучення тексту...</span>
                  </div>
                ) : null}
                
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={isUk ? 'Введіть сюди text або завантажте документ (.docx/.pdf/.txt) для автоматичного вилучення та аналізу...' : 'Type document text here or upload file to parse...'}
                  rows={10}
                  className="w-full p-3 border border-slate-200 rounded focus:outline-hidden focus:border-teal-500 text-xs font-mono leading-relaxed bg-white"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleCorrectDocument}
                disabled={isCorrecting || isExtracting || !text.trim()}
                className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 text-white font-bold font-mono text-[10px] uppercase rounded tracking-wider flex items-center justify-center gap-1.5 cursor-pointer transition-colors shadow-xs"
              >
                {isCorrecting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>{isUk ? 'АНАЛІЗУЄМО...' : 'ANALYZING...'}</span>
                  </>
                ) : (
                  <>
                    <FileCheck className="w-3.5 h-3.5" />
                    <span>{isUk ? 'ПЕРЕВІРИТИ ПРАВОПИС' : 'START PROOFREADING'}</span>
                  </>
                )}
              </button>
              
              {text && (
                <button
                  onClick={handleClear}
                  className="p-2 border border-slate-200 hover:bg-slate-50 rounded text-slate-400 hover:text-slate-700 transition-colors"
                  title={isUk ? 'Очистити все' : 'Clear all'}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 flex gap-2.5 items-start text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-[10px] font-mono leading-normal">{error}</p>
              </div>
            )}

          </div>

          {/* Info Card / Instructions */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 font-mono text-[10px] text-slate-500 space-y-3">
            <h4 className="font-bold text-slate-800 uppercase tracking-widest flex items-center gap-1.5">
              <CheckSquare className="w-4 h-4 text-teal-500" />
              <span>{isUk ? 'ПРАВИЛА ТА МЕТОДОЛОГІЯ' : 'PROOFREADING RULES'}</span>
            </h4>
            <p className="leading-relaxed">
              {isUk 
                ? 'Агент виконує повну перевірку за оновленим Українським правописом (2019 р.). До аналізу входять: орфографія, подвоєння літер, чергування голосних/приголосних, пунктуація в складних реченнях, усунення русизмів та кальок, а також стилістична корекція ділової документації.'
                : 'Checks texts using rules from the official Ukrainian Orthography (2019 reform). Scans for spelling, grammar, punctuation in complex sentences, Russianisms/clichés, and business style alignment.'}
            </p>
          </div>

        </div>

        {/* Right Column: Results - error analysis and corrected text (span 7) */}
        <div className="lg:col-span-7">
          
          {!correctedText && !isCorrecting ? (
            <div className="bg-white border border-slate-200 rounded-lg p-10 text-center space-y-4 shadow-xs">
              <div className="w-12 h-12 bg-slate-50 border border-slate-200 text-slate-400 flex items-center justify-center rounded-2xl mx-auto">
                <AlignLeft className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-bold font-mono uppercase tracking-wider text-slate-700">
                  {isUk ? 'Звіт про перевірку порожній' : 'No Analysis Performed Yet'}
                </h4>
                <p className="text-[10px] text-slate-400 font-mono leading-relaxed max-w-sm mx-auto">
                  {isUk 
                    ? 'Завантажте документ або введіть текст ліворуч, після чого натисніть «Перевірити правопис» для виявлення помилок.' 
                    : 'Upload a document or input text on the left, then click "Start Proofreading" to generate corrected results.'}
                </p>
              </div>
            </div>
          ) : isCorrecting ? (
            <div className="bg-white border border-slate-200 rounded-lg p-10 text-center space-y-6 shadow-xs flex flex-col items-center justify-center">
              <div className="relative w-14 h-14">
                <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
                <div className="absolute inset-0 rounded-full border-4 border-teal-500 border-t-transparent animate-spin" />
              </div>
              <div className="space-y-2">
                <h4 className="text-xs font-bold font-mono uppercase tracking-widest text-slate-700 animate-pulse">
                  {isUk ? 'Редагуємо та перевіряємо правила...' : 'Analyzing Grammar & Spelling...'}
                </h4>
                <p className="text-[10px] text-slate-400 font-mono leading-relaxed max-w-sm">
                  {isUk 
                    ? 'Нейромережа аналізує орфографію, узгодження відмінків, пунктуацію та лексичні кальки згідно з чинним правописом України.' 
                    : 'The model checks vocabulary, grammar cases, complex clauses, and style patterns against Ukrainian orthography norms.'}
                </p>
              </div>
            </div>
          ) : (
            // Output view
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-xs relative">
              
              {/* Output Tab bar */}
              <div className="bg-slate-50 px-4 border-b border-slate-200 flex flex-wrap items-center justify-between font-mono text-[10px] font-bold">
                <div className="flex gap-1">
                  <button
                    onClick={() => setActiveResultTab('analysis')}
                    className={`py-3 px-4 border-b-2 transition-colors cursor-pointer ${
                      activeResultTab === 'analysis'
                        ? 'border-teal-500 text-teal-600'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {isUk ? 'АНАЛІЗ ПОМИЛОК' : 'ERROR ANALYSIS'}
                  </button>
                  <button
                    onClick={() => setActiveResultTab('corrected')}
                    className={`py-3 px-4 border-b-2 transition-colors cursor-pointer ${
                      activeResultTab === 'corrected'
                        ? 'border-teal-500 text-teal-600'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {isUk ? 'ВИПРАВЛЕНИЙ ТЕКСТ' : 'CORRECTED TEXT'}
                  </button>
                </div>

                {/* Quick actions & Style Toggles */}
                <div className="flex items-center gap-2 py-2">
                  {activeResultTab === 'corrected' && (
                    <div className="flex items-center bg-slate-200/60 p-0.5 rounded text-[9px] mr-2">
                      <button
                        onClick={() => setTextStyle('dstu')}
                        className={`px-2 py-0.5 rounded font-serif transition-colors cursor-pointer ${textStyle === 'dstu' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
                        title={isUk ? 'Офіційний документ ДСТУ (Times New Roman)' : 'DSTU Document (Times)'}
                      >
                        {isUk ? 'Документ' : 'Doc'}
                      </button>
                      <button
                        onClick={() => setTextStyle('sans')}
                        className={`px-2 py-0.5 rounded font-sans transition-colors cursor-pointer ${textStyle === 'sans' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
                        title={isUk ? 'Сучасний текст (Inter)' : 'Clean Text (Sans)'}
                      >
                        {isUk ? 'Текст' : 'Clean'}
                      </button>
                      <button
                        onClick={() => setTextStyle('mono')}
                        className={`px-2 py-0.5 rounded font-mono transition-colors cursor-pointer ${textStyle === 'mono' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
                        title={isUk ? 'Моноширинний код' : 'Raw Monospace'}
                      >
                        {isUk ? 'Код' : 'Raw'}
                      </button>
                    </div>
                  )}

                  {activeResultTab === 'analysis' && errorAnalysis && (
                    <>
                      <TelegramExportButton
                        title={fileName ? `Аналіз помилок: ${fileName}` : 'Аналіз помилок документа'}
                        content={errorAnalysis}
                        agentName="Коректор ДСТУ 4163"
                        agentId="corrector"
                        isUk={isUk}
                        variant="compact"
                      />

                      <button
                        onClick={() => handleCopyText(errorAnalysis, 'analysis')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[9px] text-slate-600 hover:bg-slate-200 rounded transition-colors cursor-pointer"
                        title={isUk ? 'Копіювати звіт' : 'Copy report'}
                      >
                        {copiedAnalysis ? <Check className="w-3.5 h-3.5 text-teal-500" /> : <Copy className="w-3.5 h-3.5" />}
                        <span className="hidden sm:inline">{copiedAnalysis ? 'СКОПІЙОВАНО' : 'КОПІЮВАТИ'}</span>
                      </button>
                      
                      <button
                        onClick={handleDownloadAnalysis}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[9px] text-slate-600 hover:bg-slate-200 rounded transition-colors cursor-pointer"
                        title={isUk ? 'Завантажити Markdown' : 'Download Markdown'}
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">MD</span>
                      </button>
                    </>
                  )}

                  {activeResultTab === 'corrected' && correctedText && (
                    <>
                      <TelegramExportButton
                        title={fileName ? `Виправлений документ: ${fileName}` : 'Виправлений документ (ДСТУ 4163)'}
                        content={correctedText}
                        agentName="Коректор ДСТУ 4163"
                        agentId="corrector"
                        isUk={isUk}
                        variant="compact"
                      />

                      <button
                        onClick={() => handleCopyText(correctedText, 'text')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[9px] text-slate-600 hover:bg-slate-200 rounded transition-colors cursor-pointer"
                        title={isUk ? 'Копіювати виправлений текст' : 'Copy corrected text'}
                      >
                        {copiedText ? <Check className="w-3.5 h-3.5 text-teal-500" /> : <Copy className="w-3.5 h-3.5" />}
                        <span className="hidden sm:inline">{copiedText ? 'СКОПІЙОВАНО' : 'КОПІЮВАТИ'}</span>
                      </button>

                      <button
                        onClick={handleDownloadWord}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[9px] bg-teal-600 text-white hover:bg-teal-700 rounded transition-colors shadow-2xs font-bold cursor-pointer"
                        title={isUk ? 'Завантажити у Word (.docx)' : 'Download in Word (.docx)'}
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">WORD (.DOCX)</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Viewport for contents */}
              <div className="p-5 max-h-[600px] overflow-y-auto min-h-[350px]">
                
                {/* 1. Analysis Tab (Clean formatted report without raw MD syntax) */}
                {activeResultTab === 'analysis' && errorAnalysis && (
                  <div className="prose prose-sm font-sans text-xs text-slate-700 leading-relaxed max-w-none">
                    {renderMarkdownReport(errorAnalysis)}
                  </div>
                )}

                {/* 2. Corrected Text Tab (Clean formatted document preview) */}
                {activeResultTab === 'corrected' && correctedText && (
                  <div className="space-y-4">
                    {renderCorrectedTextPreview(correctedText)}
                  </div>
                )}

              </div>

            </div>
          )}

        </div>

      </div>

    </div>
  );
}
